import { keepPreviousData, useQueries } from "@tanstack/react-query"
import { useMemo } from "react"
import type { Edge } from "@/domains/visualization/types/Edge"
import type { Vertex } from "@/domains/visualization/types/Vertex"
import { useCmApi } from "@/shared/hooks/useCmApi"

const QUERY_KEY = "customerNeighbors"

const EMPTY_NEIGHBORS_DATA = {
  vertices: [],
  edges: []
}

export const useGetCustomerNeighbors = (
  ids: string[] = []
): {
  data: { vertices: Vertex[]; edges: Edge[] }
  isFetching: boolean
  error: Error | null
  isError: boolean
  refetch: () => void
} => {
  // Create individual queries for each selected vertex
  const cmApi = useCmApi()
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: [QUERY_KEY, id],
      queryFn: () => cmApi.getCustomerNeighbors([id]),
      placeholderData: keepPreviousData
    }))
  })

  // Combine results from all individual queries
  const combinedData = useMemo(() => {
    const allVertices: Vertex[] = []
    const allEdges: Edge[] = []

    queries.forEach((query) => {
      if (query.data?.data) {
        allVertices.push(...query.data.data.vertices)
        allEdges.push(...query.data.data.edges)
      }
    })

    return { vertices: allVertices, edges: allEdges }
  }, [queries])

  const isFetching = queries.some((query) => query.isFetching)
  const isError = queries.some((query) => query.isError)
  const error = queries.find((query) => query.error)?.error ?? null

  const refetch = () => {
    queries.forEach((query) => {
      void query.refetch()
    })
  }

  return {
    data: ids.length > 0 ? combinedData : EMPTY_NEIGHBORS_DATA,
    isFetching,
    error,
    isError,
    refetch
  }
}
